import { AlertTriangle } from "lucide-react";

import { FunnelStep, GetFunnelResponse } from "@/app/services/journeyService";
import { cn } from "@/app/utils/cn";
import { Badge } from "@/view/components/ui/badge";

const percent = (value: number) => `${(value * 100).toFixed(1)}%`;

type StepRowProps = {
  step: FunnelStep;
  baseline: number;
};

const StepRow = ({ step, baseline }: StepRowProps) => {
  const width = baseline > 0 ? (step.entered / baseline) * 100 : 0;

  const here = step.pending + step.dropped;
  const isCritical = step.entered > 0 && here / step.entered >= 0.3;

  return (
    <div className="border-b border-gray01 py-4 last:border-b-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-black04">
            {step.order}. {step.label}
          </span>
          {step.optional && (
            <Badge variant="outline" className="text-[10px]">
              opcional
            </Badge>
          )}
        </div>
        <div
          className={cn(
            "flex items-center gap-1 text-sm",
            isCritical ? "font-semibold text-red-600" : "text-black04",
          )}
          title="Total que chegou nesta etapa, e quantas continuam nela"
        >
          {isCritical && <AlertTriangle className="h-3.5 w-3.5" />}
          <span>
            Total: {step.entered}{" "}
            <span className={isCritical ? undefined : "text-gray-500"}>
              ({here} nessa etapa)
            </span>
          </span>
        </div>
      </div>

      <div className="mt-2 h-2 w-full rounded-full bg-gray01">
        <div
          className="h-2 rounded-full bg-primary transition-all"
          style={{ width: `${width}%` }}
        />
      </div>

      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
        <span>avançaram: {step.advanced}</span>
        {step.completed > 0 && <span>concluíram aqui: {step.completed}</span>}
        <span
          className={step.errors > 0 ? "text-red-600" : undefined}
          title="Etapas que terminaram em erro ou recusa do parceiro"
        >
          erros: {step.errors} ({percent(step.errorRate)})
        </span>
        <span title="Refazer a etapa é deliberação do vendedor, não erro">
          tentativas: {step.totalAttempts}
        </span>
      </div>

      {step.topErrors.length > 0 && (
        <ul className="mt-2 space-y-1">
          {step.topErrors.map((error) => (
            <li
              key={error.message}
              className="text-xs text-gray-600"
              title="Mensagem devolvida pelo parceiro"
            >
              <span className="font-semibold">{error.count}×</span>{" "}
              {error.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

type JourneyFunnelProps = {
  funnel?: GetFunnelResponse;
};

export const JourneyFunnel = ({ funnel }: JourneyFunnelProps) => {
  if (!funnel) return null;

  const baseline = funnel.steps[0]?.entered ?? 0;

  if (funnel.totals.started === 0) {
    return (
      <div className="rounded-lg border border-gray01 p-8 text-center text-sm text-gray-500">
        Nenhuma jornada iniciada para este produto no período selecionado.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray01 p-5">
      <div className="mb-4 flex flex-wrap gap-6">
        <div>
          <p className="text-xs text-gray-500">Iniciadas</p>
          <p className="text-xl font-semibold">{funnel.totals.started}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Concluídas</p>
          <p className="text-xl font-semibold text-green-600">
            {funnel.totals.completed}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Em andamento</p>
          <p className="text-xl font-semibold">{funnel.totals.pending}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Conversão</p>
          <p className="text-xl font-semibold">
            {percent(funnel.totals.conversionRate)}
          </p>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap gap-2 border-t border-gray01 pt-4">
        {funnel.steps.map((step) => (
          <div
            key={step.key}
            className="flex items-baseline gap-1.5 rounded-full border border-gray01 px-3 py-1"
            title={`Jornadas paradas em "${step.label}" (não avançaram nem concluíram)`}
          >
            <span className="text-xs text-gray-500">
              {step.order}. {step.label}
            </span>
            <span className="text-sm font-semibold">
              {step.pending + step.dropped}
            </span>
          </div>
        ))}
      </div>

      <div>
        {funnel.steps.map((step) => (
          <StepRow key={step.key} step={step} baseline={baseline} />
        ))}
      </div>
    </div>
  );
};
